<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { LucideChevronRight } from 'lucide-vue-next'

import { EntityOfferHomePagePrimaryCardDeposit } from '~/components/entity/offer/home-page'
import { WidgetHomePageBestOffersSkeleton } from '~/components/widget/best-offers'

import type { IBestOfferDeposit } from '~/components/widget/best-offers/types'
import type { IResponseData } from '~/types'
import type { IOfferPrimaryCardDeposit } from '~/components/entity/offer/home-page/primary-card/deposit'

const { t } = useI18n()

const { data: offers, status } = useApi<IResponseData<IBestOfferDeposit[]>>(
  () => '/deposits/top-deposits',
  { key: () => 'best-offer-deposits' }
)

const offersMapped = computed<IOfferPrimaryCardDeposit[]>(() => {
  return (offers.value?.data || []).map((item) => ({
    id: item.id,
    title: item.title,
    rate: t('widget.home_page.best_offers.deposit_rate', { rate: item.rate }),
    period: t('widget.home_page.best_offers.period_months', { period: item.period }),
    companyName: item.organization.title,
    companyLogo: item.organization.logo,
    url: item.url,
    isTop: true
  }))
})
</script>

<template>
  <section class="py-8">
    <header class="mb-5 flex items-center justify-between gap-4">
      <Heading tag="h2">{{ $t('widget.best_offers.deposits.heading') }}</Heading>
      <Button variant="link" size="auto" as-child>
        <NuxtLinkLocale to="/deposits">
          {{ $t('widget.best_offers.deposits.all_deposits') }}
          <LucideChevronRight />
        </NuxtLinkLocale>
      </Button>
    </header>

    <div class="mb-6">
      <p
        v-hydrated-html="$t('widget.best_offers.deposits.description')"
        class="leading-relaxed text-slate-600"
      />
    </div>

    <article>
      <div v-if="status === 'pending'">
        <div class="flex gap-4">
          <div v-for="i in 3" :key="i" class="w-[90%] flex-shrink-0 sm:w-[47%] lg:w-[31%]">
            <WidgetHomePageBestOffersSkeleton />
          </div>
        </div>
      </div>

      <div v-else-if="!offers || !offers.data || !offers.data.length" class="text-center">
        {{ $t('common.no_data') }}
      </div>

      <div v-else>
        <Swiper :space-between="16" :slides-per-view="'auto'">
          <SwiperSlide
            v-for="card in offersMapped"
            :key="card.id"
            class="!w-[90%] sm:!w-[47%] lg:!w-[31%]"
          >
            <EntityOfferHomePagePrimaryCardDeposit v-bind="card" />
          </SwiperSlide>
        </Swiper>
      </div>
    </article>
  </section>
</template>
